import { Injectable, OnDestroy } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { of, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImportMapping } from './../../models/import-mapping';
import { TransactionImportService } from './../services/transaction-import.service';
import { ImportTypeStateModel } from './import-type-state.model';
import { ImportType } from './import-type.actions';

@State<ImportTypeStateModel>({
  name: 'ImportType',
  defaults: {
    items: undefined,
    selectedItem: undefined,
    isLoading: false,
    updateId: undefined,
    deleteId: undefined,
  },
})
@Injectable()
export class ImportTypeState implements OnDestroy {
  unSubscribeAll$ = new Subject<void>();
  constructor(
    private store: Store,
    private importService: TransactionImportService
  ) {}

  @Selector()
  static getSelectedImportType(state: ImportTypeStateModel): ImportMapping {
    return state.selectedItem;
  }

  @Selector()
  static getImportTypeList(state: ImportTypeStateModel): ImportMapping[] {
    return state.items;
  }

  @Selector()
  static isLoading(state: ImportTypeStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static getUpdatedImportTypeId(state: ImportTypeStateModel): string {
    return state.updateId;
  }

  @Action(ImportType.Actions.GetImportTypes)
  getImportTypes(
    ctx: StateContext<ImportTypeStateModel>,
    action: ImportType.Actions.GetImportTypes
  ) {
    return this.importService
      .getImportMappings()
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((importTypes) => {
        // const state = ctx.getState();
        ctx.patchState({
          items: [...importTypes],
          isLoading: false,
        });
      });
  }

  @Action(ImportType.Actions.GetImportType)
  getImportType(
    ctx: StateContext<ImportTypeStateModel>,
    action: ImportType.Actions.GetImportType
  ): Subscription {
    const state = ctx.getState();
    const items = state.items;

    const importTypeIndex = items.findIndex((i) => i.id === action.id);

    if (importTypeIndex >= 0) {
      ctx.patchState({
        selectedItem: { ...items[importTypeIndex] },
        isLoading: false,
      });
      return of(items[importTypeIndex]).subscribe();
    }

    return this.importService
      .get(action.id)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((importType) => {
        ctx.patchState({
          selectedItem: importType,
          isLoading: false,
        });
      });
  }

  @Action(ImportType.Actions.InsertImportType)
  insertImportType(
    ctx: StateContext<ImportTypeStateModel>,
    action: ImportType.Actions.InsertImportType
  ) {
    return this.importService
      .create(action.importType)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((importType) => {
        const state = ctx.getState();
        const importTypes = state.items;
        ctx.patchState({
          items: [importType, ...importTypes],
          isLoading: false,
          updateId: importType.id,
        });
      });
  }

  @Action(ImportType.Actions.UpdateImportType)
  updateImportType(
    ctx: StateContext<ImportTypeStateModel>,
    action: ImportType.Actions.UpdateImportType
  ) {
    return this.importService
      .editImportMappings(action.importType)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((importType) => {
        const state = ctx.getState();
        const importTypes = [...state.items];
        const importTypeIndex = importTypes.findIndex(
          (d) => d.id === importType.id
        );

        if (importTypeIndex >= 0) {
          importTypes[importTypeIndex] = importType;
        }

        ctx.patchState({
          items: [...importTypes],
          isLoading: false,
          updateId: importType.id,
        });
      });
  }

  @Action(ImportType.Actions.DeleteImportType)
  deleteImportType(
    ctx: StateContext<ImportTypeStateModel>,
    action: ImportType.Actions.DeleteImportType
  ) {
    const importType = { ...action.importType };
    importType.status = 'Inactive';
    return this.importService
      .editImportMappings(importType)
      .pipe(takeUntil(this.unSubscribeAll$))
      .subscribe((importType2) => {
        const state = ctx.getState();
        const importTypes = [...state.items];
        const importTypeIndex = importTypes.findIndex(
          (d) => d.id === importType.id
        );

        if (importTypeIndex >= 0) {
          importTypes.splice(importTypeIndex, 1);
        }

        ctx.patchState({
          items: [...importTypes],
          updateId: action.importType.id,
          isLoading: false,
        });
      });
  }

  ngOnDestroy(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }
}
